import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Input } from 'reactstrap';
import { injectIntl } from 'react-intl';

import FileActions from '../File/Actions';
import FileDetails from '../File/Details';
import GridItemLikes from '../GridItemLikes';
import FileTitle from '../File/Title';
import TrashRemainingDays from '../TrashRemainingDays';

import {
  filesTarget,
  filesSource,
} from '../../helpers/DNDFiles';
import ItemTypes from '../../helpers/DNDItemTypes';
import { getFilePageUrl } from '../../helpers/Files';

class FilesGridItem extends PureComponent {
  componentDidMount() {
    const { connectDragPreview } = this.props;

    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true,
      });
    }
  }

  render() {
    const {
      intl,
      file,
      collection,
      shareUuid,
      shareCollectionUuid,
      share,
      connectDragSource,
      connectDropTarget,
      isDragging,
      isMoved,
      isSelected,
      onFileSelect,
      allowCheck,
      displayOnly,
      selectedView,
      renderDisplayAction,
    } = this.props;

    const fileTo = file?.to
      || getFilePageUrl({ fileUuid: file.uuid, shareUuid, shareCollectionUuid });

    const pointToNone = !!file.pointToNone;

    const isFramed = selectedView === 'framed';

    const getSrc = () => {
      if (isFramed) {
        return file.urls.thumbnailmasonry;
      }

      return file.urls.thumbnail;
    };

    const getSrcSet = () => {
      if (isFramed) {
        return `${file.urls.thumbnailmasonry} 1x`;
      }

      return `${file.urls.thumbnail} 1x, ${file.urls.thumbnailx2} 2x`;
    };

    return connectDragSource(connectDropTarget((
      <div
        id={file.uuid}
        className={classnames(
          'grid-item',
          'col-sm-12',
          {
            dragging: isDragging,
            'grid-item-point-to-none': pointToNone,
            'grid-item-framed': isFramed,
          },
        )}
      >
        {(!displayOnly && !pointToNone) && (
          <GridItemLikes
            file={file}
            share={share}
            readOnly={!!file.deleted_at}
          />
        )}
        {isMoved && (
          <span className="grid-item-moved-icon">
            <i className="fas fa-sync fa-spin" />
          </span>
        )}
        {(allowCheck && (!shareUuid || (share && share.allow_download))) && (
          <Input
            type="checkbox"
            className="input-check check-l"
            id={`file-${file.uuid}`}
            checked={isSelected}
            onChange={() => onFileSelect(file.uuid, isSelected)}
            aria-label={isSelected ? intl.formatMessage({ defaultMessage: 'Unselect' }) : intl.formatMessage({ defaultMessage: 'Select' })}
          />
        )}
        <div id={`file-element-${file.uuid}`}>
          {pointToNone && (
            <div className="grid-item-img-wrapper grid-item-img-wrapper-point-to-none">
              <img
                className="grid-item-img"
                {...(!isFramed ? { width: '100%' } : {})}
                src={getSrc()}
                srcSet={getSrcSet()}
                alt={file.display_name}
              />
            </div>
          )}
          {!pointToNone && (
            <>
              {(file.type === 'link' && !file.html_code) ? (
                <a href={file.path} className="grid-item-img-wrapper" title={file.display_name} target="_blank" rel="noopener noreferrer">
                  <img
                    className="grid-item-img"
                    {...(!isFramed ? { width: '100%' } : {})}
                    src={getSrc()}
                    srcSet={getSrcSet()}
                    alt={file.display_name}
                  />
                </a>
              ) : (
                <Link to={fileTo} className="grid-item-img-wrapper">
                  <img
                    className="grid-item-img"
                    {...(!isFramed ? { width: '100%' } : {})}
                    src={getSrc()}
                    srcSet={getSrcSet()}
                    alt={file.display_name}
                  />
                </Link>
              )}
            </>
          )}
          <div className={classnames(
            'grid-item-body',
            { 'grid-item-body-display-only': displayOnly && !renderDisplayAction },
          )}
          >
            <FileTitle
              className="grid-item-title"
              file={file}
              fileTo={fileTo}
            />
            <div className="grid-item-details-wrapper">
              <FileDetails
                className="grid-item-details"
                file={file}
                showAccess={!shareUuid && !displayOnly}
              />
              <TrashRemainingDays
                file={file}
              />
            </div>
            {(!displayOnly || renderDisplayAction) && (
              <div className="grid-item-actions">
                {!displayOnly && (
                  <FileActions
                    file={file}
                    collection={collection}
                    shareUuid={shareUuid}
                    share={share}
                  />
                )}
                {displayOnly && renderDisplayAction && (
                  <ul className="grid-item-actions-list">
                    {renderDisplayAction(file, 'grid')}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )));
  }
}

const mapStateToProps = state => ({
  collection: state.Collections.collection,
});

FilesGridItem.propTypes = {
  intl: PropTypes.object.isRequired,
  file: PropTypes.object.isRequired,
  collection: PropTypes.object,
  shareUuid: PropTypes.string,
  share: PropTypes.object,
  shareCollectionUuid: PropTypes.string,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isMoved: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  allowCheck: PropTypes.bool,
  displayOnly: PropTypes.bool,
  renderDisplayAction: PropTypes.func,
  selectedView: PropTypes.string,
};

FilesGridItem.defaultProps = {
  collection: null,
  shareUuid: null,
  share: null,
  shareCollectionUuid: null,
  allowCheck: true,
  displayOnly: false,
  renderDisplayAction: null,
  selectedView: null,
};

export default
injectIntl(withRouter(connect(mapStateToProps, null)(
  DragSource(ItemTypes.FILE, filesSource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    connectDragPreview: DNDconnect.dragPreview(),
    isDragging: monitor.isDragging(),
  }))(DropTarget([ItemTypes.FILE, ItemTypes.COLLECTION], filesTarget, DNDconnect => ({
    connectDropTarget: DNDconnect.dropTarget(),
  }))(FilesGridItem)),
)));
